import { useQuery } from '@tanstack/react-query'
import { ApiError, api } from './client'
import type { DriverSession } from './types'

/**
 * Driver endpoints the site is ready for but the API doesn't have yet. Each
 * matches a section of docs/BACKEND_REQUESTS.md. Until it exists the server
 * answers 404 and the feature hides itself or falls back (never a broken
 * screen in front of a driver).
 */

export const isMissing = (e: unknown) => e instanceof ApiError && e.isMissing

// ── D10. Help and problem reports (dashboard B19) ───────────────────────
export type SupportKind = 'problem' | 'payment' | 'account' | 'other'
export interface SupportMessage {
  id: number
  from: 'driver' | 'staff'
  author_name: string | null
  body: string
  photo_urls: string[]
  created_at: string
}
export interface SupportThread {
  id: number
  kind: SupportKind
  subject: string
  status: 'open' | 'waiting' | 'resolved'
  station_id: number | null
  station_name: string | null
  charger_id: string | null
  connector_number: number | null
  session_id: number | null
  created_at: string
  updated_at: string
  /** A staff reply the driver hasn't opened yet. */
  unread: boolean
  messages?: SupportMessage[]
}
export interface NewSupport {
  kind: SupportKind
  subject: string
  message: string
  station_id?: number
  charger_id?: string
  connector_number?: number
  session_id?: number
  photos?: File[]
}
const supportForm = (b: NewSupport) => {
  const f = new FormData()
  for (const [k, v] of Object.entries(b)) if (v != null && k !== 'photos') f.append(k, String(v))
  b.photos?.forEach((p) => f.append('photos', p))
  return f
}
export const support = {
  list: () => api.get<{ threads: SupportThread[] }>('/user/support'),
  get: (id: number) => api.get<SupportThread>(`/user/support/${id}`),
  create: (b: NewSupport) => api.post<SupportThread>('/user/support', undefined, { form: supportForm(b) }),
  reply: (id: number, body: string) => api.post<SupportMessage>(`/user/support/${id}/messages`, { body }),
}

// ── D11. Reviews and driver photos (dashboard B20) ──────────────────────
export interface Review {
  id: number
  rating: number
  body: string | null
  photo_urls: string[]
  author_name: string
  created_at: string
  reply: { body: string; created_at: string } | null
  mine?: boolean
}
export const reviews = {
  forStation: (stationId: number) => api.get<{ average: number | null; count: number; reviews: Review[] }>(`/public/stations/${stationId}/reviews`),
  write: (stationId: number, b: { rating: number; body?: string; session_id?: number; photos?: File[] }) => {
    const f = new FormData()
    f.append('rating', String(b.rating))
    if (b.body) f.append('body', b.body)
    if (b.session_id) f.append('session_id', String(b.session_id))
    b.photos?.forEach((p) => f.append('photos', p))
    return api.post<Review>(`/user/stations/${stationId}/reviews`, undefined, { form: f })
  },
}
export const useStationReviews = (stationId: number) => useQuery({ queryKey: ['reviews', stationId], queryFn: () => reviews.forStation(stationId), retry: false, staleTime: 5 * 60_000 })

// ── D12. Favourites ─────────────────────────────────────────────────────
export const favourites = {
  list: () => api.get<{ station_ids: number[] }>('/user/favourites'),
  add: (stationId: number) => api.put(`/user/favourites/${stationId}`),
  remove: (stationId: number) => api.del(`/user/favourites/${stationId}`),
}

// ── D13. Notify me when a connector is free ─────────────────────────────
export interface Watch {
  id: number
  station_id: number
  connector_type: string | null
  expires_at: string
}
export const watches = {
  list: () => api.get<{ watches: Watch[] }>('/user/watches'),
  create: (b: { station_id: number; connector_type?: string | null; minutes?: number }) => api.post<Watch>('/user/watches', b),
  remove: (id: number) => api.del(`/user/watches/${id}`),
}

// ── D14. Receipts ───────────────────────────────────────────────────────
export const receipts = {
  session: (id: number) => api.get<DriverSession>(`/user/sessions/${id}`),
  email: (id: number) => api.post(`/user/sessions/${id}/email-receipt`),
}

// ── D15. Routes ─────────────────────────────────────────────────────────
export interface Route {
  distance_km: number
  duration_min: number
  /** [lat, lng] points along the road. */
  geometry: [number, number][]
}
export const routing = {
  route: (from: [number, number], to: [number, number]) => api.get<Route>('/public/route', { from: from.join(','), to: to.join(',') }),
}
